import React, { useState, useCallback } from 'react'
import { Header, MobileDrawer } from './index'
import { Footer } from './Footer'
import { cn } from '@/utils/cn'
import { useTheme } from '@/hooks/useTheme'

export interface LayoutProps {
  children: React.ReactNode
  className?: string
  showHeader?: boolean
  showFooter?: boolean
  fullWidth?: boolean
  headerProps?: React.ComponentProps<typeof Header>
  mainClassName?: string
  showSkipLink?: boolean
}

const Layout: React.FC<LayoutProps> = ({
  children,
  className,
  showHeader = true,
  showFooter = true,
  fullWidth = false,
  headerProps,
  mainClassName,
  showSkipLink = true,
}) => {
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false)
  const { isDark } = useTheme()

  // Mobile Drawer Handlers
  const handleMenuToggle = useCallback(() => {
    setIsMobileDrawerOpen(prev => !prev)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setIsMobileDrawerOpen(false)
  }, [])

  return (
    <div className={cn(
        className,
        isDark ? 'bg-secondary-900' : 'bg-white'
      )}>
      {/* Skip Link for Accessibility */}
      {showSkipLink && (
        <a
          href="#main-content"
          className={cn(
            'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 px-4 py-2 rounded z-50',
            'bg-primary-600 text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-500'
          )}
        >
          Skip to main content
        </a>
      )}

      {/* Header Integration */}
      {showHeader && (
        <Header
          {...headerProps}
          onMenuToggle={handleMenuToggle}
          className={cn(headerProps?.className)}
        />
      )}

      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={isMobileDrawerOpen}
        onClose={handleDrawerClose}
      />

      {/* Main Content */}
      <main
        id="main-content"
        role="main"
        className={cn(
          'min-h-screen',
          showHeader ? 'pt-24' : '', // Add top padding when header is shown to account for fixed header
          isDark ? 'bg-secondary-900' : 'bg-white',
          !fullWidth && 'container mx-auto px-4',
          fullWidth && 'w-full',
          mainClassName
        )}
      >
        {children}
      </main>

      {showFooter && <Footer />}
    </div>
  )
}

export { Layout }